import { Globe, LayoutDashboard, Trash2, UserRound } from 'lucide-react';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import { MetaChip } from '@/_shared/components/list/MetaChip';
import type { TableColumn } from '@/_shared/components/Table';
import { Badge } from '@/_shared/components/ui/badge';
import { timeAgo } from '@/_shared/lib/format';

import { type DashboardListViewItem, panelCountLabel } from '../lib/dashboardList';
import { StarToggleButton } from './StarToggleButton';

interface BuildColumnsOptions {
  onDelete: (id: string) => void;
  /** Challenge-mode creator name resolver, or null when attribution is off. */
  creatorNameFor: ((userId: string | null) => string | null) | null;
  onToggleStar: (id: string, nextStarred: boolean) => void;
  isStarPending?: (id: string) => boolean;
}

/**
 * Columns for the dashboard list's table view (header hidden). Rows are pre-sorted upstream (the toolbar
 * drives one sort for both views), so the columns are pure renderers.
 */
export function buildDashboardColumns({
  onDelete,
  creatorNameFor,
  onToggleStar,
  isStarPending,
}: BuildColumnsOptions): TableColumn<DashboardListViewItem>[] {
  return [
    {
      id: 'starred',
      header: '',
      headerAriaLabel: 'Starred',
      width: '2.5rem',
      align: 'center',
      cell: (d) => (
        <StarToggleButton
          dashboardTitle={d.title}
          starred={d.isStarred}
          loading={isStarPending?.(d.id) ?? false}
          onToggle={() => onToggleStar(d.id, !d.isStarred)}
        />
      ),
    },
    {
      id: 'title',
      header: '',
      headerAriaLabel: 'Name, description, and panel count',
      activatesRow: true,
      activatesRowLabel: (d) => d.title,
      wrap: true,
      // All-phrasing (`<span>`s) so the cell is valid content inside the Table's row-activator `<button>`.
      cell: (d) => {
        const creator = creatorNameFor?.(d.createdBy) ?? null;
        return (
          <span className="block min-w-0">
            <span title={d.title} className="block truncate font-medium text-foreground">
              {d.title}
            </span>
            {d.description && (
              <span title={d.description} className="mt-0.5 line-clamp-2 text-xs font-normal text-muted-foreground">
                {d.description}
              </span>
            )}
            <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-normal text-muted-foreground">
              <MetaChip icon={LayoutDashboard}>{panelCountLabel(d.spec.panels.length)}</MetaChip>
              {creator && <MetaChip icon={UserRound}>{creator}</MetaChip>}
              <NeedsRepairBadge listRow state={d.accountRepair.state} />
            </span>
          </span>
        );
      },
    },
    {
      id: 'visibility',
      header: '',
      headerAriaLabel: 'Visibility',
      width: '6rem',
      wrap: true,
      cell: (d) =>
        d.isPublic ? (
          <Badge variant="info" bordered className="shrink-0">
            <Globe className="size-3" />
            Public
          </Badge>
        ) : null,
    },
    {
      id: 'activity',
      header: '',
      headerAriaLabel: 'Last updated',
      width: '10rem',
      wrap: true,
      // No icon: this column renders one line, so an icon would decorate rather than differentiate.
      // (Workflows keeps icons — its activity column stacks "updated" and "last run".)
      cell: (d) => (
        <div className="truncate text-xs font-normal text-muted-foreground">Updated {timeAgo(d.updatedAt)}</div>
      ),
    },
    {
      ...ROW_ACTIONS_COLUMN,
      cell: (d) => (
        <KebabMenu
          label={`Actions for ${d.title}`}
          actions={[{ label: 'Delete', icon: Trash2, destructive: true, onSelect: () => onDelete(d.id) }]}
        />
      ),
    },
  ];
}
